import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useI18n } from "@/contexts/I18nContext";
import { toast } from "sonner";
import {
  Sparkles, Lightbulb, ListTodo, AlertTriangle, RefreshCw, ArrowLeft,
} from "lucide-react";

/**
 * DAAM Intelligence — Daily AI Recommendations
 * Renders the "🤖 توصيات دعم اليوم" section at the top of the Dashboard.
 * Cards are generated by the AI marketing manager. All copy stays in Arabic.
 */

// Whitelist of routes that actually exist in the app. If the AI ever returns
// a route outside this set, we fall back to a sensible default per card type
// instead of navigating to a broken page.
const VALID_ROUTES = new Set([
  "/app/dashboard", "/app/assistant", "/app/studio", "/app/social",
  "/app/calendar", "/app/analytics", "/app/reports", "/app/team",
  "/app/plans", "/app/billing", "/app/profile", "/app/settings",
]);

const FALLBACK_ROUTE_BY_TYPE = {
  opportunity: "/app/analytics",
  content: "/app/studio",
  task: "/app/studio",
  alert: "/app/social",
};

function resolveRoute(card) {
  const r = card?.cta_route;
  if (r && VALID_ROUTES.has(r)) return r;
  return FALLBACK_ROUTE_BY_TYPE[card?.type] || "/app/dashboard";
}

const CARD_META = {
  opportunity: {
    Icon: Sparkles,
    accent: "#B8875D", // bronze
    ring: "rgba(184,135,93,0.30)",
    bg: "rgba(184,135,93,0.06)",
    fallbackTitle: "فرصة اليوم",
  },
  content: {
    Icon: Lightbulb,
    accent: "#5B8DEF",
    ring: "rgba(91,141,239,0.30)",
    bg: "rgba(91,141,239,0.06)",
    fallbackTitle: "فكرة محتوى اليوم",
  },
  task: {
    Icon: ListTodo,
    accent: "#10B981",
    ring: "rgba(16,185,129,0.30)",
    bg: "rgba(16,185,129,0.06)",
    fallbackTitle: "مهمة اليوم",
  },
  alert: {
    Icon: AlertTriangle,
    accent: "#F59E0B",
    ring: "rgba(245,158,11,0.30)",
    bg: "rgba(245,158,11,0.06)",
    fallbackTitle: "تنبيه",
  },
};

function CardSkeleton() {
  return (
    <div className="nx-card p-5 h-40 animate-pulse" data-testid="daam-recommendation-skeleton">
      <div className="h-4 w-24 bg-muted/50 rounded-sm mb-4" />
      <div className="h-3 w-full bg-muted/40 rounded-sm mb-2" />
      <div className="h-3 w-3/4 bg-muted/40 rounded-sm mb-6" />
      <div className="h-3 w-20 bg-muted/40 rounded-sm" />
    </div>
  );
}

function RecommendationCard({ card, index }) {
  const nav = useNavigate();
  const meta = CARD_META[card.type] || CARD_META.opportunity;
  const Icon = meta.Icon;
  const title = card.title || meta.fallbackTitle;
  // Every card is clickable — either uses AI-provided route (if valid) or a per-type fallback.
  const route = resolveRoute(card);

  const openDetail = () => {
    nav(route);
  };

  return (
    <div
      data-testid={`daam-recommendation-${card.type}`}
      role="button"
      tabIndex={0}
      onClick={openDetail}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          openDetail();
        }
      }}
      className="nx-card p-5 relative overflow-hidden group cursor-pointer hover:-translate-y-0.5 hover:shadow-lg transition-all focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/50"
      style={{ animationDelay: `${index * 80}ms` }}
    >
      <div
        className="absolute inset-x-0 top-0 h-0.5"
        style={{ background: meta.accent }}
        aria-hidden
      />
      <div className="flex items-start gap-3 mb-3">
        <div
          className="w-9 h-9 rounded-sm flex items-center justify-center shrink-0"
          style={{ background: meta.bg, color: meta.accent, boxShadow: `inset 0 0 0 1px ${meta.ring}` }}
        >
          <Icon className="w-4 h-4" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-[10px] uppercase tracking-widest" style={{ color: meta.accent }}>
            {card.type === "opportunity" && "فرصة"}
            {card.type === "content" && "فكرة محتوى"}
            {card.type === "task" && "مهمة"}
            {card.type === "alert" && "تنبيه"}
          </div>
          <div className="font-display text-lg mt-0.5 leading-tight">{title}</div>
        </div>
      </div>
      <p className="text-sm text-foreground/85 leading-relaxed mb-4">{card.body}</p>
      {card.cta_label && (
        <span
          data-testid={`daam-recommendation-cta-${card.type}`}
          className="inline-flex items-center gap-1.5 text-xs font-medium group-hover:underline"
          style={{ color: meta.accent }}
        >
          {card.cta_label}
          <ArrowLeft className="w-3.5 h-3.5" />
        </span>
      )}
    </div>
  );
}

export default function DailyRecommendations() {
  const { lang } = useI18n();
  const [state, setState] = useState({ loading: true, cards: [], generatedAt: null, error: null });
  const [refreshing, setRefreshing] = useState(false);

  const load = async () => {
    setState((s) => ({ ...s, loading: true, error: null }));
    try {
      const { data } = await api.get("/dashboard/daily-recommendations");
      setState({ loading: false, cards: data.cards || [], generatedAt: data.generated_at, error: null });
    } catch (e) {
      setState({ loading: false, cards: [], generatedAt: null, error: e?.message || "load_failed" });
    }
  };

  useEffect(() => { load(); }, []);

  const refresh = async () => {
    setRefreshing(true);
    try {
      const { data } = await api.post("/dashboard/daily-recommendations/refresh");
      setState({ loading: false, cards: data.cards || [], generatedAt: data.generated_at, error: null });
      toast.success(lang === "ar" ? "تم تحديث توصيات اليوم" : "Today's recommendations refreshed");
    } catch {
      toast.error(lang === "ar" ? "تعذّر التحديث" : "Refresh failed");
    } finally {
      setRefreshing(false);
    }
  };

  return (
    <section data-testid="daam-intelligence-section" className="mb-8" dir="rtl">
      <div className="flex items-center justify-between mb-4">
        <div>
          <h2 data-testid="daam-intelligence-title" className="font-display text-xl md:text-2xl flex items-center gap-2">
            <span aria-hidden>🤖</span>
            <span>توصيات دعم اليوم</span>
          </h2>
          <p className="text-xs text-muted-foreground mt-1">
            مدير التسويق الذكي الخاص بك — توصيات مخصصة لنشاطك التجاري.
          </p>
        </div>
        <button
          data-testid="daam-intelligence-refresh"
          onClick={refresh}
          disabled={refreshing || state.loading}
          className="inline-flex items-center gap-1.5 text-xs text-muted-foreground hover:text-foreground border border-border/60 rounded-sm px-3 py-1.5 disabled:opacity-50"
        >
          <RefreshCw className={`w-3.5 h-3.5 ${refreshing ? "animate-spin" : ""}`} />
          <span>{refreshing ? "جاري التحديث..." : "تحديث"}</span>
        </button>
      </div>

      {/* Error state */}
      {state.error && !state.loading && (
        <div data-testid="daam-intelligence-error" className="nx-card p-6 border border-destructive/30 bg-destructive/5">
          <div className="text-sm text-foreground/85 mb-3">
            تعذّر تحميل توصيات اليوم. حاول مرة أخرى.
          </div>
          <button
            data-testid="daam-intelligence-retry"
            onClick={load}
            className="text-xs text-primary hover:underline inline-flex items-center gap-1.5"
          >
            <RefreshCw className="w-3.5 h-3.5" /> إعادة المحاولة
          </button>
        </div>
      )}

      {/* Empty state (loaded but no cards returned) */}
      {!state.loading && !state.error && state.cards.length === 0 && (
        <div data-testid="daam-intelligence-empty" className="nx-card p-6 text-sm text-muted-foreground text-center">
          لا توجد توصيات اليوم. اربط حسابك على TikTok لتلقي توصيات ذكية.
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4">
        {state.loading && Array.from({ length: 4 }).map((_, i) => <CardSkeleton key={i} />)}
        {!state.loading && !state.error && state.cards.map((card, i) => (
          <RecommendationCard key={`${card.type}-${i}`} card={card} index={i} />
        ))}
      </div>
    </section>
  );
}
